import {
    type TextStyle,
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    TreeViewRowColorsConstantType,
    TreeViewRowIndentConstantType,
    TreeViewRowDynamicStyleType,
    TreeViewRowColors
} from "./type";
import type {
    Mutable
} from "../../../../types";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";
import {
    TREE_VIEW_SELECTED_VARIANT
} from "../../stylesheet";
import {
    webStyle
} from "../../../../utils";

export const getTreeViewRowIndent = ({
    measures,
    level
}: TreeViewRowIndentConstantType): number => {
    return measures.paddingStart + Math.max(level - 1, 0) * measures.indent;
};

export const getTreeViewRowColors = ({
    isDisabled,
    isSelected,
    isActive,
    isError,
    colors
}: TreeViewRowColorsConstantType): TreeViewRowColors => {
    if(isDisabled) {
        const disabledColor = colors.system.state.content.disabled.neutral;

        return {
            toggleColor: disabledColor,
            containerColor: undefined,
            iconColor: disabledColor,
            textColor: disabledColor,
            metaColor: disabledColor
        };
    }

    if(isError) {
        return {
            iconColor: colors.content.icon.danger,
            textColor: colors.content.text.danger,
            toggleColor: colors.content.icon.mid,
            metaColor: colors.content.text.mid,
            containerColor: undefined
        };
    }

    if(isSelected) {
        return {
            containerColor: colors.content.container.selected,
            toggleColor: colors.content.icon.high,
            iconColor: colors.content.icon.high,
            textColor: colors.content.text.high,
            metaColor: colors.content.text.mid
        };
    }

    return {
        metaColor: isActive ? colors.content.text.mid : colors.content.text.low,
        toggleColor: colors.content.icon.mid,
        textColor: colors.content.text.high,
        iconColor: colors.content.icon.mid,
        containerColor: undefined
    };
};

const stylesheet = StyleSheet.create({
    container: {
        flexDirection: "row",
        alignItems: "center",
        position: "relative",
        minWidth: 0
    },
    meta: {
        fontVariant: [
            "tabular-nums"
        ],
        flexShrink: 0
    },
    iconSlot: {
        flexShrink: 0
    },
    drill: {
        flexShrink: 0
    },
    label: {
        minWidth: 0,
        flex: 1
    }
});

export const useStyles = ({
    isFocusVisible,
    currentColors,
    shapeConfigs,
    isDisabled,
    isSelected,
    typography,
    measures,
    borders,
    colors,
    level
}: TreeViewRowDynamicStyleType) => {
    const styles = {
        container: {
            paddingStart: getTreeViewRowIndent({
                measures,
                level
            }),
            paddingEnd: measures.paddingEnd,
            ...getCornerStyle({
                radius: measures.radius,
                shapeConfigs
            }),
            minHeight: measures.rowHeight,
            gap: measures.gap,
            ...webStyle({
                cursor: isDisabled ? "not-allowed" : "pointer",
                userSelect: "none"
            })
        } as Mutable<ViewStyle>,
        label: {} as Mutable<TextStyle>
    };

    if(currentColors.containerColor) {
        styles.container.backgroundColor = currentColors.containerColor;
    }

    if(isSelected) {
        Object.assign(styles.label, {
            fontFamily: typography[TREE_VIEW_SELECTED_VARIANT].fontFamily,
            fontWeight: typography[TREE_VIEW_SELECTED_VARIANT].fontWeight
        });
    }

    Object.assign(styles.container, getFocusRingStyle({
        isFocusVisible,
        isInset: true,
        borders,
        colors
    }));

    return styles;
};
export default stylesheet;
